/**
 * BuildingDialog — o balão de fala do EDIFÍCIO ("recepcionista do prédio").
 *
 * Quando o parceiro aciona uma interação com um prédio (evento `announce` do
 * backend), o prédio "fala" para TODOS os viewers da cidade: um balão de diálogo
 * ancorado ao prédio, com uma seta (rabinho) apontando para ele — como se um
 * recepcionista estivesse anunciando algo do balcão.
 *
 * É um overlay em DOM (texto nítido, fácil de estilizar) projetado a cada frame a
 * partir da posição 3D do prédio (mesma técnica de projeção do de-clutter de
 * etiquetas no EntityManager). Cada prédio tem NO MÁXIMO um balão ativo; uma nova
 * fala do mesmo prédio substitui a anterior. O texto é "digitado" (efeito de fala)
 * e o balão some sozinho depois de um tempo proporcional ao tamanho da mensagem.
 *
 * Ancorado ao renderer só pela câmera (recebida em update) — não depende do
 * EntityManager nem cria nada na cena 3D. Em modo simulado (?sim, sem backend) ele
 * fica ocioso; dá para testá-lo pelo console: __wabs.announcements.show({...}).
 */
import * as THREE from 'three';
import { BUILDINGS, BUILDING_INDEX } from '../world/cityLayout.js';
import { SAY_TONE } from '../../shared/protocol.js';

const TYPE_MS = 26;      // ms por caractere na "digitação" (efeito de fala)
// Duração PADRÃO que a mensagem fica na tela DEPOIS de terminar de digitar (ms).
// ▶ Ajuste esta variável para deixar as falas mais curtas/longas. Um `durationMs`
//   no evento do backend sobrepõe este padrão para uma mensagem específica.
const MESSAGE_DURATION_MS = 5000;
const HIDE_MS = 340;     // duração da animação de SAÍDA (keyframe bdlg-out no hud.css)
const MARGIN = 14;       // folga p/ manter o balão dentro da tela (px)
// Altura da ÂNCORA na fachada, como fração da altura do prédio (0 = base, 1 = topo).
// A seta aponta para esse ponto e o balão sobe a partir dele. Mais BAIXO (≈meio) faz
// o balão subir menos → tapa menos o prédio de TRÁS. ▶ ajuste aqui p/ subir/descer.
const ANCHOR_H = 0.5;

// Cor PADRÃO da bolha: BRANCA (como as caixas de mensagem do WhatsApp hoje). O
// backend pode enviar `color` (#rrggbb) para trocar; o contraste do texto se ajusta
// sozinho (texto escuro em bolha clara, claro em bolha escura — ver isLightColor).
const DEFAULT_BUBBLE = '#ffffff';
// tom → cor do NOME do remetente, em 2 variantes conforme a bolha seja clara/escura
const TONE_COLORS = {
  [SAY_TONE.NEUTRAL]: { onLight: '#1f9dd6', onDark: '#8fd4f0' },
  [SAY_TONE.HAPPY]: { onLight: '#12a150', onDark: '#7ff0b0' },
  [SAY_TONE.SAD]: { onLight: '#2f7fd6', onDark: '#8fb8ff' },
  [SAY_TONE.ALERT]: { onLight: '#dd6f14', onDark: '#ffb35e' }
};

export class BuildingDialog {
  constructor(parent = document.getElementById('hud') || document.body) {
    this.root = document.createElement('div');
    this.root.className = 'bdlg-layer';
    parent.appendChild(this.root);
    this._active = new Map(); // idx do prédio → estado do balão
    this._v = new THREE.Vector3();
  }

  /**
   * Mostra (ou atualiza) a fala de um prédio.
   * @param {object} a  { building, text, title?, tone?, durationMs? }
   *   building = id de grade (A1..C5) OU índice do prédio nesta cidade.
   */
  show(a) {
    if (!a || !a.text) return;
    const idx = this._resolveIndex(a.building);
    if (idx == null) { // prédio não existe nesta cidade → ignora de boa
      if (typeof console !== 'undefined') console.debug('[BuildingDialog] prédio desconhecido:', a.building);
      return;
    }
    const b = BUILDINGS[idx];
    // texto em "runs" (trechos normais/negrito): *frase* ou **frase** vira NEGRITO,
    // e os asteriscos são removidos. A digitação conta só os caracteres visíveis.
    const runs = parseRuns(String(a.text));
    const len = runs.reduce((acc, r) => acc + r.chars.length, 0);
    // tempo na tela após digitar: `durationMs` do backend sobrepõe o padrão
    const hold = a.durationMs || MESSAGE_DURATION_MS;
    // "quem fala" (title): SÓ exibe se o backend mandar — sem fallback p/ o nome do prédio
    const title = typeof a.title === 'string' ? a.title.trim() : '';

    let st = this._active.get(idx);
    if (!st) {
      st = this._create(idx);
      this._active.set(idx, st);
    }
    // conteúdo + horário (estilo WhatsApp)
    st.headEl.textContent = title;
    st.headEl.style.display = title ? '' : 'none'; // sem title → nem mostra o cabeçalho
    st.timeEl.textContent = fmtTime();
    st.runs = runs;
    st.len = len;
    st.textEl.textContent = '';
    // cor da bolha: `color` do backend (padrão BRANCO). O texto/horário se ajustam
    // ao contraste (escuros em bolha clara, claros em escura) e o NOME usa o tom.
    const bubble = normalizeColor(a.color) || DEFAULT_BUBBLE;
    const light = isLightColor(bubble);
    const tc = TONE_COLORS[a.tone] || TONE_COLORS[SAY_TONE.NEUTRAL];
    const s = st.el.style;
    s.setProperty('--bdlg-bg', bubble);
    s.setProperty('--bdlg-text', light ? '#111b21' : '#e9edef');
    s.setProperty('--bdlg-accent', light ? tc.onLight : tc.onDark);
    // âncora 3D: fachada frontal, à MEIA-ALTURA do prédio (ANCHOR_H) — a seta aponta
    // para esse ponto e o balão sobe menos, evitando tapar o prédio de trás.
    st.ax = b.x;
    st.ay = b.h * ANCHOR_H;
    st.az = b.z + b.d / 2;
    // (re)inicia tempos e a animação de entrada
    st.start = performance.now();
    st.typeDur = len * TYPE_MS;
    st.until = st.start + st.typeDur + hold;
    st.hideAt = null;
    st.shown = -1;
    st.el.classList.remove('is-hide');
    st.el.classList.remove('is-show');
    void st.el.offsetWidth; // reflow → reinicia a entrada mesmo p/ o mesmo balão
    st.el.classList.add('is-show');
  }

  /**
   * Projeta e posiciona os balões ativos; avança a digitação e expira os antigos.
   *
   * TRÊS PASSADAS, na ordem escrever → LER TUDO → escrever tudo.
   *
   * A versão anterior fazia tudo num laço só: escrevia `innerHTML` e `display`,
   * depois lia `offsetWidth`/`offsetHeight`, depois escrevia `left`/`top` — e a
   * volta seguinte escrevia de novo. Ler uma medida de layout depois de ter escrito
   * obriga o navegador a recalcular o layout NA HORA, de forma síncrona; num laço,
   * isso vira um recálculo por balão por quadro (o "layout thrashing"). Medido em
   * Chrome real: ~1,2 ms por quadro no pior caso (13–15 balões, o teto de prédios
   * de uma cidade).
   *
   * Separando as fases, todas as leituras caem depois de todas as escritas da fase
   * anterior e compartilham UM único recálculo. O `Hud._playFlip` já fazia assim —
   * este método passa a seguir o mesmo padrão.
   *
   * O que NÃO mudou: `renderRuns` continua remontando a string por caractere. Foi
   * medido e custa ~4 µs por passo (0,2 ms somados numa mensagem inteira) — o
   * problema nunca foi esse custo, e sim ele sujar o layout no meio das leituras.
   * Agora ele está na 1ª passada, antes de qualquer leitura.
   */
  update(camera) {
    if (!camera || !this._active.size) return;
    const now = performance.now();
    const W = window.innerWidth, H = window.innerHeight;
    const medidas = this._medidas || (this._medidas = []); // reaproveitado
    medidas.length = 0;

    // ---- 1ª passada: só ESCRITA (expira, remove, digita, projeta) ----
    for (const [idx, st] of this._active) {
      // expira → dispara a saída → remove ao fim da animação
      if (st.hideAt == null && now >= st.until) {
        st.hideAt = now;
        st.el.classList.remove('is-show');
        st.el.classList.add('is-hide');
      }
      if (st.hideAt != null && now >= st.hideAt + HIDE_MS) {
        this.root.removeChild(st.el);
        this._active.delete(idx);
        continue;
      }

      // digitação (efeito de fala): revela caractere a caractere (trechos em negrito)
      const n = st.hideAt != null
        ? st.len // ao sair, mostra tudo (não "des-digita")
        : clampInt(Math.floor((now - st.start) / TYPE_MS), 0, st.len);
      if (n !== st.shown) { st.textEl.innerHTML = renderRuns(st.runs, n); st.shown = n; }

      // projeta a âncora do prédio na tela (matemática pura, não toca no DOM)
      this._v.set(st.ax, st.ay, st.az).project(camera);
      if (this._v.z > 1) { st.el.style.display = 'none'; continue; } // atrás da câmera
      st.el.style.display = '';
      medidas.push(st, (this._v.x * 0.5 + 0.5) * W, (-this._v.y * 0.5 + 0.5) * H, 0, 0);
    }

    // ---- 2ª passada: só LEITURA (um recálculo de layout para todos) ----
    for (let i = 0; i < medidas.length; i += 5) {
      const el = medidas[i].el;
      medidas[i + 3] = el.offsetWidth;
      medidas[i + 4] = el.offsetHeight;
    }

    // ---- 3ª passada: só ESCRITA das posições ----
    for (let i = 0; i < medidas.length; i += 5) {
      const st = medidas[i];
      const sx = medidas[i + 1], sy = medidas[i + 2];
      // o balão fica ACIMA do ponto (transform translate(-50%,-100%) no CSS): a
      // ponta da seta encosta em (sx, sy). Mantém dentro da tela (clamp suave).
      const hw = medidas[i + 3] / 2;
      const hh = medidas[i + 4];
      st.el.style.left = clamp(sx, hw + MARGIN, W - hw - MARGIN) + 'px';
      st.el.style.top = clamp(sy, hh + MARGIN, H - MARGIN) + 'px';
    }
  }

  /** Remove todos os balões (troca de cidade / limpeza). */
  clear() {
    for (const [, st] of this._active) if (st.el.parentNode) this.root.removeChild(st.el);
    this._active.clear();
  }

  /* ------------------------------- interno ------------------------------- */

  _resolveIndex(building) {
    if (typeof building === 'number') return BUILDINGS[building] ? building : null;
    const idx = BUILDING_INDEX.get(building);
    return idx == null ? null : idx;
  }

  _create(idx) {
    const el = document.createElement('div');
    el.className = 'bdlg';
    const box = document.createElement('div');
    box.className = 'bdlg__box';
    // o texto e o ellipsis ficam no PRÓPRIO .bdlg__head (sem span interno) — assim
    // dá para OCULTAR o cabeçalho inteiro (com o padding) quando não há `title`.
    const head = document.createElement('div');
    head.className = 'bdlg__head';
    const text = document.createElement('div');
    text.className = 'bdlg__text';
    // rodapé estilo WhatsApp: horário + tiques de "lida" (✓✓)
    const meta = document.createElement('div');
    meta.className = 'bdlg__meta';
    const time = document.createElement('span');
    time.className = 'bdlg__time';
    const ticks = document.createElement('span');
    ticks.className = 'bdlg__ticks';
    ticks.innerHTML = '<svg viewBox="0 0 18 12" width="17" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6.5 L5 9.5 L10.5 3"/><path d="M6.5 6.5 L9.5 9.5 L15 3"/></svg>';
    meta.append(time, ticks);
    box.append(head, text, meta);
    // o rabinho (seta) é feito com ::after do .bdlg__box (ver hud.css) — triângulo
    // sólido na cor da bolha, sem borda/sombra própria (bolha do WhatsApp não tem borda).
    el.append(box);
    this.root.appendChild(el);
    return { idx, el, boxEl: box, headEl: head, textEl: text, timeEl: time };
  }
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function clampInt(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
/** Horário HH:MM (relógio local) exibido no rodapé da bolha, como no WhatsApp. */
function fmtTime(d = new Date()) {
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

/**
 * Divide o texto em "runs" (trechos), marcando NEGRITO o que vier entre asteriscos:
 * `*frase*` ou `**frase**` (estilo WhatsApp). Os asteriscos são removidos; asteriscos
 * soltos (sem par) ficam literais. Cada run guarda os chars por CODE POINT (emoji ok).
 */
function parseRuns(text) {
  const runs = [];
  const re = /\*\*([^*\n]+)\*\*|\*([^*\n]+)\*/g; // **negrito** OU *negrito*
  let last = 0, m;
  const push = (str, bold) => { if (str) runs.push({ chars: [...str], b: bold }); };
  while ((m = re.exec(text)) !== null) {
    push(text.slice(last, m.index), false);
    push(m[1] != null ? m[1] : m[2], true);
    last = m.index + m[0].length;
  }
  push(text.slice(last), false);
  return runs;
}

/** Monta o HTML revelando os primeiros `n` caracteres visíveis, aplicando <b> aos
 *  trechos em negrito. Escapa o conteúdo (o texto vem do backend). */
function renderRuns(runs, n) {
  let out = '', count = 0;
  for (const r of runs) {
    if (count >= n) break;
    const take = Math.min(r.chars.length, n - count);
    count += take;
    const safe = escapeHtml(r.chars.slice(0, take).join(''));
    out += r.b ? '<b>' + safe + '</b>' : safe;
  }
  return out;
}
function escapeHtml(s) {
  return s.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));
}
/** #rgb ou #rrggbb válido → devolve normalizado (#rrggbb); senão null (cai no padrão). */
function normalizeColor(c) {
  if (typeof c !== 'string') return null;
  const s = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s;
  if (/^#[0-9a-f]{3}$/i.test(s)) return '#' + s.slice(1).split('').map((h) => h + h).join('');
  return null;
}
/** Bolha "clara" (texto escuro) vs "escura" (texto claro), por luminância perceptual. */
function isLightColor(hex) {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}
